import { ApiProperty, ApiPropertyOptional } from '@nestjs/swagger';

/**
 * DTO para una fila del Excel Consolidado de Órdenes de Transporte Agencia.
 * Una fila por contenedor/patente (no por orden de transporte), replicando el modelo
 * de columnas del cliente (`EXCEL CONSOLIDADO.xlsx`, 45 columnas tras excluir "Cut Off Doc",
 * que no tiene origen identificable en el esquema — ver feat-transporte-consolidado-timeline).
 *
 * @example
 * ```json
 * {
 *   "TransportOrderId": 3538,
 *   "ContainerNumber": "MNBU4243466",
 *   "TruckLicensePlate": "CLPB80",
 *   "MontoNeto": "$ 645.000 CLP"
 * }
 * ```
 */
export class OrdenesTransporteConsolidadoItemDto {
  // Identificación
  @ApiProperty({ description: 'Número de Orden de Transporte', example: 3538 })
  TransportOrderId!: number;

  @ApiPropertyOptional({ description: 'Número de liquidación', example: 513 })
  NroLiquidacion?: number | null;

  @ApiPropertyOptional({ description: 'Número de booking', example: 'C987032077' })
  Booking?: string | null;

  @ApiPropertyOptional({ description: 'Referencia del cliente', example: 'M-M229-1' })
  RefCliente?: string | null;

  // Cliente / Exportador
  @ApiPropertyOptional({ description: 'Nombre del cliente', example: 'PRONTO' })
  Cliente?: string | null;

  @ApiPropertyOptional({ description: 'Nombre del exportador', example: 'PRONTO' })
  Exportador?: string | null;

  // Nave / Naviera / Ruta
  @ApiPropertyOptional({ description: 'Nombre de la nave', example: 'POLAR ARGENTINA' })
  Nave?: string | null;

  @ApiPropertyOptional({ description: 'Naviera', example: 'MAERSK CHILE S.A.' })
  CarrierName?: string | null;

  @ApiPropertyOptional({ description: 'Puerto de origen (POL)', example: 'VALPARAISO' })
  PortOfLoadingName?: string | null;

  @ApiPropertyOptional({ description: 'Terminal', example: 'TPS' })
  TerminalName?: string | null;

  @ApiPropertyOptional({ description: 'Puerto de destino (POD)', example: 'PUERTO QUETZAL' })
  PortOfDischargeName?: string | null;

  @ApiPropertyOptional({ description: 'País de destino', example: 'GUATEMALA' })
  CountryOfDischargeName?: string | null;

  // Fechas generales
  @ApiPropertyOptional({ description: 'Fecha ETD formateada (DD/MM/YYYY)', example: '28/07/2026' })
  ETD?: string | null;

  @ApiPropertyOptional({ description: 'Tipo de embarque', example: 'Contenedor' })
  TipoEmbarque?: string | null;

  // Carga y Contenedor
  @ApiPropertyOptional({ description: 'Número de contenedor', example: 'MNBU4243466' })
  ContainerNumber?: string | null;

  @ApiPropertyOptional({ description: 'Sello', example: 'MLCL0744855' })
  CarrierSealNumber?: string | null;

  @ApiPropertyOptional({ description: 'Servicio convencional (S/Conv)', example: 'No' })
  ShipConvenienceIndicatorName?: string | null;

  @ApiPropertyOptional({ description: 'Cantidad de pallets', example: 20 })
  QuantityOfPallets?: number | null;

  @ApiPropertyOptional({ description: 'Tara del contenedor (formateada)', example: '4.470' })
  ContainerTare?: string | null;

  @ApiPropertyOptional({
    description: 'Especies (agregado — un contenedor puede llevar más de una)',
    example: 'Fresh Oranges',
  })
  MerchandiseName?: string | null;

  // Transporte Terrestre
  @ApiPropertyOptional({ description: 'Patente del camión', example: 'CLPB80' })
  TruckLicensePlate?: string | null;

  @ApiPropertyOptional({ description: 'Depósito', example: 'CONTOPSA VAP' })
  DepotName?: string | null;

  @ApiPropertyOptional({ description: 'Planta de despacho', example: 'GIOIA' })
  FirstDispatchPlantName?: string | null;

  @ApiPropertyOptional({ description: 'Localidad de la planta de despacho', example: 'SAN FELIPE' })
  FirstDispatchPlantLocationName?: string | null;

  @ApiPropertyOptional({ description: 'Fecha de citación en planta (formateada)' })
  FirstDispatchPlantCitationDate?: string | null;

  // Trazabilidad (con duraciones)
  @ApiPropertyOptional({ description: 'Llegada a depósito (formateada)' })
  GateInDepotDate?: string | null;

  @ApiPropertyOptional({ description: 'Salida de depósito (formateada)' })
  GateOutDepotDate?: string | null;

  @ApiPropertyOptional({
    description: 'Duración en depósito, horas con 1 decimal (vacío si falta alguna fecha del par)',
    example: 1.3,
  })
  DuracionDepositoHrs?: number | null;

  @ApiPropertyOptional({ description: 'Entrada a planta de despacho (formateada)' })
  GateInDate?: string | null;

  @ApiPropertyOptional({ description: 'Salida de planta de despacho (formateada)' })
  GateOutDate?: string | null;

  @ApiPropertyOptional({
    description: 'Duración en planta, horas con 1 decimal (vacío si falta alguna fecha del par)',
    example: 1.4,
  })
  DuracionPlantaHrs?: number | null;

  @ApiPropertyOptional({ description: 'Fecha de visación — inspección SAG (formateada)' })
  SAGCustomsInspectionDate?: string | null;

  @ApiPropertyOptional({ description: 'Llegada a puerto / antepuerto (formateada)' })
  GateInTerminalDate?: string | null;

  @ApiPropertyOptional({ description: 'Entregado en stacking (formateada)' })
  GateInStackingDate?: string | null;

  @ApiPropertyOptional({ description: 'Salida de terminal / puerto (formateada)' })
  GateOutTerminalDate?: string | null;

  @ApiPropertyOptional({
    description: 'Duración en terminal (Entregado Stacking → Salida Puerto), horas con 1 decimal',
    example: 2.1,
  })
  DuracionTerminalHrs?: number | null;

  // Porteo e indicadores de pago
  @ApiPropertyOptional({ description: 'Tipo de servicio de porteo', example: 'No' })
  TypeOfServiceName?: string | null;

  @ApiPropertyOptional({ description: 'Quién paga el Gate Out', example: 'Cliente' })
  GateOutDepotPayerIndicatorName?: string | null;

  @ApiPropertyOptional({ description: 'Estado de pago del Gate Out', example: 'Pendiente' })
  GateOutDepotPaymentIndicatorName?: string | null;

  @ApiPropertyOptional({ description: 'Quién paga el Gate In' })
  GateInDepotPayerIndicatorName?: string | null;

  @ApiPropertyOptional({ description: 'Estado de pago del Gate In' })
  GateInDepotPaymentIndicatorName?: string | null;

  // Financiero (por contenedor, no repetido por O/T)
  @ApiPropertyOptional({ description: 'Monto neto del contenedor (formateado)', example: '$ 645.000 CLP' })
  MontoNeto?: string | null;

  @ApiPropertyOptional({ description: 'Extra costos del contenedor (formateado)', example: '$ 149.000 CLP' })
  ExtraCostos?: string | null;

  @ApiPropertyOptional({ description: 'SubTotal del contenedor (formateado)', example: '$ 794.000 CLP' })
  SubTotal?: string | null;

  // Comentarios
  @ApiPropertyOptional({ description: 'Comentarios de control de transporte' })
  RemarksTransportControl?: string | null;
}
